const REFERRAL_COOKIE_NAMESPACE = "uncreator";
const REFERRAL_COOKIE_PURPOSE = "referral";
export const PENDING_REFERRAL_COOKIE = [
  REFERRAL_COOKIE_NAMESPACE,
  REFERRAL_COOKIE_PURPOSE,
].join("_");
export const PENDING_REFERRAL_MAX_AGE_SECONDS = 15 * 60;
const REFERRAL_CODE_PATTERN = /^[A-Za-z0-9_-]{16,64}$/;

type BrowserLocation = Pick<
  Location,
  "hostname" | "pathname" | "protocol" | "search" | "hash"
>;

type ReferralBrowser = {
  document: Pick<Document, "cookie">;
  history: Pick<History, "replaceState" | "state">;
  location: BrowserLocation;
};

const isUncreatorClubHost = (hostname: string) =>
  hostname === "uncreator.club" || hostname.endsWith(".uncreator.club");

export const isReferralCode = (value: unknown): value is string =>
  typeof value === "string" && REFERRAL_CODE_PATTERN.test(value);

const cookieAttributes = (
  location: Pick<BrowserLocation, "hostname" | "protocol">,
  maxAge = PENDING_REFERRAL_MAX_AGE_SECONDS,
) => {
  const hostname = location.hostname.toLowerCase();
  const production = isUncreatorClubHost(hostname);
  const secure = production || location.protocol === "https:";
  const attributes = [
    `Max-Age=${maxAge}`,
    "Path=/",
    "SameSite=Lax",
    production ? "Domain=.uncreator.club" : "",
    secure ? "Secure" : "",
  ];

  return attributes.filter(Boolean).join("; ");
};

export const readPendingReferralCode = (cookie: string): string | undefined => {
  const encodedValue = cookie
    .split(";")
    .map((part) => part.trim())
    .find((part) => part.startsWith(`${PENDING_REFERRAL_COOKIE}=`))
    ?.slice(PENDING_REFERRAL_COOKIE.length + 1);

  if (!encodedValue) return undefined;

  try {
    const referralCode = decodeURIComponent(encodedValue);
    return isReferralCode(referralCode) ? referralCode : undefined;
  } catch {
    return undefined;
  }
};

export const getPendingReferralCode = (
  browser: Pick<ReferralBrowser, "document">,
) => readPendingReferralCode(browser.document.cookie);

export const setPendingReferralCode = (
  referralCode: string,
  browser: Pick<ReferralBrowser, "document" | "location">,
) => {
  if (!isReferralCode(referralCode)) return false;
  browser.document.cookie = `${PENDING_REFERRAL_COOKIE}=${referralCode}; ${cookieAttributes(browser.location)}`;
  return true;
};

export const clearPendingReferralCode = (
  browser: Pick<ReferralBrowser, "document" | "location">,
) => {
  browser.document.cookie = `${PENDING_REFERRAL_COOKIE}=; ${cookieAttributes(browser.location, 0)}`;
};

/**
 * A Creator command captures its referral before it is frozen. Do not erase a
 * referral that was captured later by a subsequent landing-page visit.
 */
export const clearPendingReferralCodeIfMatches = (
  expectedReferralCode: string,
  browser: Pick<ReferralBrowser, "document" | "location">,
) => {
  if (
    !isReferralCode(expectedReferralCode) ||
    getPendingReferralCode(browser) !== expectedReferralCode
  )
    return false;
  clearPendingReferralCode(browser);
  return true;
};

export const capturePendingReferralFromUrl = (browser: ReferralBrowser) => {
  const query = new URLSearchParams(browser.location.search);
  if (!query.has("ref")) return;

  const referralCode = query.get("ref");
  const storedReferralCode =
    referralCode && setPendingReferralCode(referralCode, browser)
      ? referralCode
      : undefined;

  query.delete("ref");
  const search = query.toString();
  browser.history.replaceState(
    browser.history.state,
    "",
    `${browser.location.pathname}${search ? `?${search}` : ""}${browser.location.hash}`,
  );
  return storedReferralCode;
};
